// Form to set a discount

import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import Spinner from './Spinner';
import * as Yup from 'yup';
import InputMoney from './InputMoney';
import MultiSelect from './MultiSelect';
import { useModal } from './ModalProvider';
import { tournamentTeamUpdate } from '../utils/firebase';
import { useTeam, useTeamsDirectory } from '../utils/hooks';
import { getTeamDivision } from '../utils/misc';
import './SetDiscount.scss';

export default function SetDiscount({ id, team, discount }) {
  const [spinner, setSpinner] = useState(false);
  // The directory is for pickers; one team's name is a read by id, which
  // the directory contract keeps off it
  const { teams } = useTeamsDirectory(!team);
  const { entity: singleTeam } = useTeam(team);
  const { closeModal } = useModal();

  // Build team options: label = "Name (Division)", value = team id; sorted by division then name
  const teamOptions = useMemo(
    () =>
      [...teams]
        .sort((a, b) => {
          const divCmp = getTeamDivision(a).localeCompare(getTeamDivision(b));
          if (divCmp !== 0) return divCmp;
          return a.name.localeCompare(b.name);
        })
        .map((t) => ({
          value: t.id,
          label: `${t.name} (${getTeamDivision(t)})`,
        })),
    [teams],
  );

  const initialValues = {
    discount: discount ?? '',
    teams: [],
  };

  const validationSchema = new Yup.ObjectSchema({
    discount: Yup.number()
      .typeError('Must be a number')
      .min(0, 'Must be positeve or zero')
      .required('Required'),
    teams: !team
      ? Yup.array().min(1, 'Required').required('Required')
      : Yup.array(),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setSpinner(true);
      try {
        if (values.teams.length > 0) {
          // Bulk: update all selected teams
          const batch = values.teams.map((teamId) =>
            tournamentTeamUpdate({
              id,
              team: teamId,
              discount: +values.discount,
            }),
          );
          await Promise.all(batch);
        } else {
          // Single-team discount
          await tournamentTeamUpdate({
            id,
            team,
            discount: +values.discount,
          });
        }
        formik.resetForm();
        closeModal();
      } catch (error) {
        toast.error(error?.message ?? 'Failed to set discount');
      } finally {
        setSpinner(false);
      }
    },
  });

  return (
    <form className='set-discount' onSubmit={formik.handleSubmit}>
      {/* If in bulk mode — show team multi-selector */}
      {!team && (
        <>
          <p className='font-16'>Set a bulk discount for selected teams</p>
          <MultiSelect
            options={teamOptions}
            value={formik.values.teams}
            onChange={(val) => formik.setFieldValue('teams', val)}
            placeholder='Select teams'
            search
            error={
              formik.touched.teams && formik.errors.teams
                ? formik.errors.teams
                : null
            }
          />
        </>
      )}
      {/* If single team mode — show team name */}
      {team && (
        <p className='font-16'>
          Set discount for{' '}
          <span className='font-medium'>{singleTeam?.name}</span>:
        </p>
      )}
      <InputMoney
        name='discount'
        placeholder='Discount'
        value={formik.values.discount}
        onChange={(value) => formik.setFieldValue('discount', value)}
        onBlur={formik.handleBlur}
        error={
          formik.touched.discount && formik.errors.discount
            ? formik.errors.discount
            : null
        }
      />
      <div className='btns-wrap'>
        <button
          className='bw'
          type='button'
          onClick={() => {
            closeModal();
            formik.resetForm();
          }}
        >
          Cancel
        </button>
        <button type='submit'>Set</button>
      </div>
      <Spinner show={spinner} />
    </form>
  );
}
